import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  DirectionsRun as DirectionsRunIcon,
  BarChart as BarChartIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarTodayIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Description as DescriptionIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Group as GroupIcon,
  Monitor as MonitorIcon,
  Flag as FlagIcon,


  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
  children?: NavItem[];
}

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useSelector((state: RootState) => state.auth);

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>({});

  const handleToggleSection = (label: string) => {
    setOpenSections(prev => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const getNavigationItems = (): NavItem[] => {
    const items: NavItem[] = [];

    // Athlete Navigation
    if (role === 'athlete' || role === 'both') {
      items.push(
        {
          label: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/athlete/dashboard',
        },
        {
          label: 'Activities',
          icon: <DirectionsRunIcon />,
          path: '/athlete/activities',
        },
        {
          label: 'Analytics',
          icon: <BarChartIcon />,
          path: '/athlete/analytics',
        },
        {
          label: 'Workouts',
          icon: <CalendarTodayIcon />,
          path: '/athlete/workouts',
        },
        {
          label: 'My Trainers',
          icon: <PeopleIcon />,
          path: '/athlete/trainers',
        },

      );
    }

    // Trainer Navigation
    if (role === 'trainer' || role === 'both') {
      if (items.length > 0) {
        items.push({ label: 'divider', icon: <></>, path: '' });
      }

      items.push(
        {
          label: 'Athletes',
          icon: <GroupIcon />,
          path: '/trainer/athletes',
        },
        {
          label: 'Workout Templates',
          icon: <FitnessCenterIcon />,
          path: '/trainer/templates',
        },
        {
          label: 'Assign Workout',
          icon: <PersonAddIcon />,
          path: '/trainer/assign',
        }
      );
    }

    // Admin Navigation
    if (role === 'superuser') {
      if (items.length > 0) {
        items.push({ label: 'divider', icon: <></>, path: '' });
      }

      items.push(
        {
          label: 'Admin Dashboard',
          icon: <AdminPanelSettingsIcon />,
          path: '/admin/dashboard',
        },
        {
          label: 'User Management',
          icon: <GroupIcon />,
          path: '/admin/users',
        },
        {
          label: 'System Health',
          icon: <MonitorIcon />,
          path: '/admin/health',
        },
        {
          label: 'Shards Management',
          icon: <DescriptionIcon />,
          path: '/admin/shards',
        },
        {
          label: 'Feature Flags',
          icon: <FlagIcon />,
          path: '/admin/flags',
        }
      );
    }

    // Common items for all authenticated users
    if (items.length > 0) {
      items.push({ label: 'divider', icon: <></>, path: '' });
    }

    items.push({
      label: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
    });

    return items;
  };

  const navigationItems = getNavigationItems();

  const isActive = (path: string) => {
    if (path === '') return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigate = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const renderNavItem = (item: NavItem, depth = 0) => {
    if (item.label === 'divider') {
      return <Divider key={`divider-${Math.random()}`} sx={{ my: 1 }} />;
    }

    const active = isActive(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openSections[item.label];

    return (
      <React.Fragment key={item.path || item.label}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleToggleSection(item.label);
              } else {
                handleNavigate(item.path);
              }
            }}
            sx={{
              minHeight: 48,
              justifyContent: collapsed ? 'center' : 'initial',
              px: collapsed ? 0 : 2.5,
              pl: !collapsed && depth > 0 ? 4 : collapsed ? 0 : 2.5,
              backgroundColor: active ? 'primary.main' : 'transparent',
              color: active ? 'primary.contrastText' : 'text.primary',
              '&:hover': {
                backgroundColor: active ? 'primary.dark' : 'action.hover',
              },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}
          >
            <Tooltip title={collapsed ? item.label : ''} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: active ? 'primary.contrastText' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </Tooltip>
            {!collapsed && (
              <>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 400,
                  }}
                />
                {item.badge !== undefined && item.badge > 0 && (
                  <Box
                    sx={{
                      backgroundColor: 'error.main',
                      color: 'white',
                      borderRadius: '10px',
                      px: 1,
                      py: 0.25,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      minWidth: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
                {hasChildren && (
                  isOpen ? <ExpandLess /> : <ExpandMore />
                )}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && !collapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderNavItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        transition: 'width 0.3s ease',
      }}
    >
      {/* Sidebar Header with Toggle */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            Navigation
          </Typography>
          {onToggleCollapse && (
            <IconButton size="small" onClick={onToggleCollapse}>
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Box>
      )}

      {collapsed && onToggleCollapse && (
        <Box
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <IconButton size="small" onClick={onToggleCollapse}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      {/* Navigation List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List>
          {navigationItems.map(item => renderNavItem(item))}
        </List>
      </Box>

      {/* Sidebar Footer */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Role: {role === 'both'
              ? 'Athlete & Trainer'
              : role
                ? role.charAt(0).toUpperCase() + role.slice(1)
                : 'Guest'
            }
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            v1.0.0
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;